import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';
import lodashIsEmpty from 'lodash/fp/isEmpty';
import lodashValues from 'lodash/fp/values';

import { SR_ERROR_USER_ALREADY_EXISTS } from 'common/errorCodes';
import { USER_ROLE_ANONYMOUS } from 'common/userRoles';
import typeCheck from 'common/util/typeCheck';
import getNumbersRange from 'common/util/getNumbersRange';

import { shouldUserSwitchToApartments } from 'common/leadTypeHelpers';
import { isListingTenantScreening } from 'common/listingHelpers';
import { SR_API_ACTION_SIGNUP } from 'api/actionTypes';

import withScrollToTop from 'client/helpers/withScrollToTop';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import branch from 'client/core/branch';
import { getUserLandingPage } from 'client/helpers/allowedRoles';
import { RENTER_START_TOURING_OPTION_TODAY } from 'client/helpers/startTouringOptionItems';

import SrLink from 'client/components/SrLink/SrLink';
import OnboardingActions from 'client/actions/Onboarding';
import AuthActions from 'client/actions/Auth';
import TourActions from 'client/actions/Tour';
import UserActions from 'client/actions/User';
import MarketActions from 'client/actions/Market';
import HouseholdActions from 'client/actions/Household';
import ListingActions from 'client/actions/Listing';
import Navbar from 'client/components/Navbar';

import OnboardingStep, {
  SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT,
  SIGNUP_ONBOARDING_FLOW_VARIANT_LONG,
  SIGNUP_ONBOARDING_FLOW_VARIANT_ALTERNATE,
  SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT_V2,
} from 'client/components/OnboardingStep';
import BackButton from 'client/components/BackButton';
import ActivityIndicator from 'client/components/ActivityIndicator';
import SrEnablePushNotificationsModal from 'client/components/SrEnablePushNotificationsModal';

import {
  SIGNUP_REASON_TOUR,
  SIGNUP_REASON_BOOKMARK,
  SIGNUP_REASON_WANT_TO_APPLY,
  SIGNUP_REASON_START_PREQUAL,
  SIGNUP_REASON_APARTMENT_LOCATOR_INVITE,
  SIGNUP_REASON_SIGNUP_PAGE,
  SIGNUP_REASON_SELF_TOURING,
  SIGNUP_REASON_TOUR_WAITLIST,
  EXTENDED_SIGNUP_V1,
} from 'common/onboardingConstants';
import onboardingSteps from './onboardingSteps';

const isUserAlreadyExistsError = (error) => error && error.name === SR_ERROR_USER_ALREADY_EXISTS;
const isNonUniqueEmailError = (error) => {
  return error && error.constraint === 'users_email_unique';
};

export function makeSignupReasonFromLocationQuery(locationQuery) {
  typeCheck('locationQuery::Object', locationQuery);
  let signupReason;
  if (locationQuery.tourRequested) {
    signupReason = SIGNUP_REASON_TOUR;
  } else if (locationQuery.wantsToApply && locationQuery.listingId) {
    signupReason = SIGNUP_REASON_WANT_TO_APPLY;
  } else if (locationQuery.startPrequal && locationQuery.listingId) {
    signupReason = SIGNUP_REASON_START_PREQUAL;
  } else if (locationQuery.bookmarkRequested) {
    signupReason = SIGNUP_REASON_BOOKMARK;
  } else if (locationQuery.invitedBy) {
    signupReason = SIGNUP_REASON_APARTMENT_LOCATOR_INVITE;
  } else if (locationQuery.extended) {
    signupReason = EXTENDED_SIGNUP_V1;
  } else if (locationQuery.selfTouring && locationQuery.listingId) {
    signupReason = SIGNUP_REASON_SELF_TOURING;
  } else if (locationQuery.joinWaitlist && locationQuery.listingId) {
    signupReason = SIGNUP_REASON_TOUR_WAITLIST;
  } else {
    signupReason = SIGNUP_REASON_SIGNUP_PAGE;
  }
  return signupReason;
}

const formItemToStepNumberMappingByOnboardingFlowVariant = {
  [SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT]: [
    'propertyTypes',
    'propertyPrices',
    'moveInDate',
    'leaseTerm',
    'marketId',
    'name',
    'email',
    'phoneNumber',
    'password',
  ],
  [SIGNUP_ONBOARDING_FLOW_VARIANT_LONG]: [
    'propertyTypes',
    'bedrooms',
    'propertyPrices',
    'leaseSituation',
    'moveInDate',
    'leaseTerm',
    'petCount',
    'pets',
    'marketId',
    'area',
    'startTouring',
    'name',
    'email',
    'phoneNumber',
    'password',
  ],
  [SIGNUP_ONBOARDING_FLOW_VARIANT_ALTERNATE]: [
    'propertyTypes',
    'bedroomsMulti',
    'propertyPricesRange',
    'leaseSituation',
    'moveDateRange',
    'leaseTerm',
    'petCount',
    'marketId',
    'area',
    'startTouring',
    'laundryPreference',
    'roommateCount',
    'creditScore',
    'name',
    'email',
    'phoneNumber',
    'password',
  ],
  [SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT_V2]: [
    'moveInDate',
    'name',
    'email',
    'phoneNumber',
    'password',
  ],
};

@withScrollToTop
@withTransitionHook
@withApiState({
  onboardingApiState: {
    action: SR_API_ACTION_SIGNUP,
  },
})
@branch({
  onboarding: ['onboarding'],
  currentUser: ['currentUser'],
  isMobileApp: ['mobileApp', 'isMobileApp'],
  askedPushNotificationPermission: ['mobileApp', 'askedPushNotificationPermission'],
  marketsById: ['markets', 'byId'],
  listing: ['listing'],
})
class OnboardingPage extends React.Component {
  static getPageMetadata = () => ({
    pageTitle: 'Signup to see Homes & Apartments | Sunroom Rentals',
    section: 'Onboarding',
    // canonical: '/',
    keywords: 'rent, home, austin, tour, apartment',
    description:
      'Spend less time scheduling tours, our rental experts ' +
      'will schedule and confirm tours for you.',
  });

  static propTypes = {
    onboarding: PropTypes.object.isRequired,
    onboardingApiState: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    isMobileApp: PropTypes.bool.isRequired,
    askedPushNotificationPermission: PropTypes.bool.isRequired,
    marketsById: PropTypes.object.isRequired,
  };

  static fetchData(state, inject) {
    const locationQuery = queryString.parse(inject.location.search);

    return Promise.all([
      MarketActions.getList(inject),
      locationQuery.listingId && ListingActions.getById(inject, locationQuery.listingId),
    ]);
  }

  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
      showEnablePushNotificationModal: false,
    };
    this._unmounting = false;
    this._onSubmitStep = this._onSubmitStep.bind(this);
    this._onSubmitOnboarding = this._onSubmitOnboarding.bind(this);
  }

  componentDidMount() {
    const { dispatch, location } = this.props;
    const locationQuery = queryString.parse(location.search);

    if (locationQuery.marketId) {
      dispatch(OnboardingActions.setValues, {
        marketId: parseInt(locationQuery.marketId, 10),
      });
    }
  }

  UNSAFE_componentWillReceiveProps() {
    global.scrollTo(0, 0);
  }

  _getOnboardingFlowVariant = () => {
    const { location, listing } = this.props;

    const locationQuery = queryString.parse(location.search);
    const signupReason = makeSignupReasonFromLocationQuery(locationQuery);

    switch (signupReason) {
      case SIGNUP_REASON_TOUR:
      case SIGNUP_REASON_WANT_TO_APPLY:
      case SIGNUP_REASON_BOOKMARK: {
        return SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT;
      }
      case SIGNUP_REASON_START_PREQUAL: {
        if (listing && isListingTenantScreening(listing)) {
          return SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT_V2;
        }
        return SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT;
      }
      case SIGNUP_REASON_TOUR_WAITLIST:
      case SIGNUP_REASON_SELF_TOURING: {
        return SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT_V2;
      }
      case EXTENDED_SIGNUP_V1: {
        return SIGNUP_ONBOARDING_FLOW_VARIANT_ALTERNATE;
      }
      default: {
        return SIGNUP_ONBOARDING_FLOW_VARIANT_LONG;
      }
    }
  };

  _getMapFormItemsToStepNumber = () => {
    const onboardingFlowVariant = this._getOnboardingFlowVariant();
    return formItemToStepNumberMappingByOnboardingFlowVariant[onboardingFlowVariant];
  };

  async _onSubmitOnboarding({ formData, mapFormItemToStepNumber }) {
    const { location, dispatch, isMobileApp, askedPushNotificationPermission } = this.props;
    const locationQuery = queryString.parse(location.search);

    this.setState({
      isSubmitting: true,
    });
    const payload = { ...formData };
    const signupReason = makeSignupReasonFromLocationQuery(locationQuery);
    const onboardingFlowVariant = this._getOnboardingFlowVariant();

    if (onboardingFlowVariant === SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT_V2) {
      const listing = await dispatch(ListingActions.getById, locationQuery.listingId);
      const market = listing.marketId
        ? await dispatch(MarketActions.getById, { marketId: listing.marketId })
        : null;
      payload.propertyTypes = [];
      payload.bedrooms = [];
      payload.marketId = listing.marketId;
      if (market) {
        payload.area = market.settings.map.area;
      }
      payload.startTouring = RENTER_START_TOURING_OPTION_TODAY;
    } else if (locationQuery.tourRequested) {
      const listing = await dispatch(ListingActions.getById, locationQuery.tourRequested);
      if (listing) {
        payload.listingId = listing.id;
        if (!payload.bathrooms) {
          payload.bathrooms = Math.floor(listing.bathrooms);
        }
        if (!payload.bedrooms) {
          payload.bedrooms = [Math.floor(listing.bedrooms)];
        }
      }
    }

    let minRentPrice;
    let maxRentPrice;

    if (formData.propertyPricesRange) {
      minRentPrice = formData.propertyPricesRange[0];
      maxRentPrice = formData.propertyPricesRange[1];
    } else if (formData.propertyPrices) {
      const rentRange = getNumbersRange(formData.propertyPrices);
      minRentPrice = rentRange.lowestNumber;
      maxRentPrice = rentRange.isRange ? rentRange.highestNumber : null;
    }

    const signupData = {
      moveInDate: formData.moveDateRange && formData.moveDateRange[1],
      minRentPrice: minRentPrice,
      maxRentPrice: maxRentPrice,
      ...payload,
      onboardingSteps: mapFormItemToStepNumber,
      signupReason: signupReason,
      apartmentLocatorUserId: locationQuery.invitedBy
        ? parseInt(locationQuery.invitedBy, 10)
        : null,
    };

    const newUser = await dispatch(AuthActions.signup, signupData);

    if (newUser && isMobileApp && !askedPushNotificationPermission) {
      this.setState({
        showEnablePushNotificationModal: true,
      });
    } else if (!newUser) {
      this.setState({
        isSubmitting: false,
      });
    } else {
      this._onNextSignupAction(formData);
    }
  }

  async _onNextSignupAction(formData) {
    const { location, history, dispatch, currentUser } = this.props;

    const locationQuery = queryString.parse(location.search);
    const signupReason = makeSignupReasonFromLocationQuery(locationQuery);
    if (signupReason === EXTENDED_SIGNUP_V1) {
      dispatch(HouseholdActions.updateRenterProfile, {
        userId: currentUser.id,
        updates: {
          prequalRoommateCount: formData.roommateCount,
          petCount: formData.roommateCount,
          movePeriodStartDate: formData.moveDateRange[0],
          movePeriodEndDate: formData.moveDateRange[1],
          creditScore: formData.creditScore,
        },
      });
    }
    if (signupReason === SIGNUP_REASON_SIGNUP_PAGE || signupReason === EXTENDED_SIGNUP_V1) {
      if (shouldUserSwitchToApartments(currentUser)) {
        return history.push('/start/switch-to-apartments');
      }

      this.setState({
        isSubmitting: false,
      });
    } else if (signupReason === SIGNUP_REASON_APARTMENT_LOCATOR_INVITE) {
      this.setState({
        isSubmitting: false,
      });
    } else {
      switch (signupReason) {
        case SIGNUP_REASON_TOUR: {
          dispatch(TourActions.requestShowingStepSubmit, {
            listingId: parseInt(locationQuery.tourRequested, 10),
            locationQuery: locationQuery,
          });
          break;
        }
        case SIGNUP_REASON_WANT_TO_APPLY: {
          dispatch(HouseholdActions.roommateApplicationStep, {
            listingId: parseInt(locationQuery.listingId, 10),
          });
          break;
        }
        case SIGNUP_REASON_START_PREQUAL: {
          dispatch(HouseholdActions.startPrequal, {
            userId: currentUser.id,
            listingId: parseInt(locationQuery.listingId, 10),
          });
          break;
        }
        case SIGNUP_REASON_BOOKMARK: {
          await dispatch(UserActions.addBookmark, {
            listingId: parseInt(locationQuery.bookmarkRequested, 10),
          });
          history.push('/saved');
          break;
        }
        case SIGNUP_REASON_SELF_TOURING: {
          history.push(`/listing/${locationQuery.listingId}?source=unlockListing`);
          break;
        }
        case SIGNUP_REASON_TOUR_WAITLIST: {
          history.push(`/listing/${locationQuery.listingId}?source=joinWailist`);
          break;
        }
      }
    }
  }

  _onCloseEnablePushNotificationsModal = () => {
    const { location } = this.props;
    const locationQuery = queryString.parse(location.search);

    if (locationQuery.tourRequested || locationQuery.bookmarkRequested) {
      this._onNextSignupAction();
    } else {
      this.setState({ showEnablePushNotificationModal: false, isSubmitting: false });
    }
  };

  async _onSubmitStep(formKey, formValue) {
    const { onboarding, dispatch } = this.props;

    const mapFormItemToStepNumber = this._getMapFormItemsToStepNumber();
    const numberOfSteps = mapFormItemToStepNumber.length;
    const nextFormKey =
      onboarding.currentStepNumber !== numberOfSteps
        ? mapFormItemToStepNumber[onboarding.currentStepNumber]
        : '';

    const formData = dispatch(OnboardingActions.saveStep, {
      formKey,
      formValue,
      numberOfSteps,
      nextFormKey,
    });

    if (onboarding.currentStepNumber === numberOfSteps) {
      this._onSubmitOnboarding({ formKey, formData, mapFormItemToStepNumber });
    }
  }

  _backToStep = () => {
    const { dispatch, onboarding } = this.props;

    const mapFormItemToStepNumber = this._getMapFormItemsToStepNumber();
    const petStepNumber = mapFormItemToStepNumber.indexOf('pets');
    const areaStepNumber = mapFormItemToStepNumber.indexOf('area');

    if (
      petStepNumber !== -1 &&
      onboarding.currentStepNumber === petStepNumber + 2 &&
      onboarding.formData.petCount === 0
    ) {
      dispatch(OnboardingActions.backToStep, onboarding.currentStepNumber - 2);
    }
    // NOTE(@archr): Skip "choose area" step for Dallas
    // PR: https://github.com/SunroomOrg/web/pull/1714
    else if (
      areaStepNumber !== -1 &&
      onboarding.currentStepNumber === areaStepNumber + 2 &&
      onboarding.formData.marketId === 2
    ) {
      dispatch(OnboardingActions.backToStep, onboarding.currentStepNumber - 2);
    } else {
      dispatch(OnboardingActions.backToStep, onboarding.currentStepNumber - 1);
    }
  };

  render() {
    const { location, currentUser, onboarding, onboardingApiState, marketsById } = this.props;

    const { isSubmitting, showEnablePushNotificationModal } = this.state;

    if (!isSubmitting && !currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
      return (
        <Redirect
          to={getUserLandingPage({
            userRoles: currentUser.roles,
          })}
        />
      );
    }

    const locationQuery = queryString.parse(location.search);
    const mapFormItemToStepNumber = this._getMapFormItemsToStepNumber();
    const formItemId = mapFormItemToStepNumber[onboarding.currentStepNumber - 1];
    const formItem = onboardingSteps[formItemId];
    const onboardingFlowVariant = this._getOnboardingFlowVariant();
    const progressPercentage =
      (onboarding.currentStepNumber / mapFormItemToStepNumber.length) * 100;
    let leftButtonDOM = null;

    if (onboarding.currentStepNumber > 1 && !onboardingApiState.inProgress) {
      leftButtonDOM = <BackButton onClick={this._backToStep} />;
    } else if (locationQuery.tourRequested || locationQuery.selfTouring) {
      leftButtonDOM = (
        <SrLink
          className="link back"
          to={locationQuery.tourRequested ? '/feed' : `/get-started${location.search}`}
          restoreScrollPosition
          redirectToLastLocation
        />
      );
    }

    const loginUrl = `/login${location.search}`;

    return (
      <div className="page-start">
        <div className={formItemId}>
          <div className="progressBarContainer">
            <div className="progressBar" style={{ width: `${progressPercentage}%` }} />
          </div>
          <Navbar redirectLogoTo="/" leftButton={leftButtonDOM} />
          {isUserAlreadyExistsError(onboardingApiState.error) && (
            <div className="formMessage error">
              <span>
                Whoops, there is already an account with that phone number. Please&nbsp;&nbsp;
                <SrLink to={loginUrl}>log&nbsp;in</SrLink>.
              </span>
            </div>
          )}
          {isNonUniqueEmailError(onboardingApiState.error) && (
            <div className="formMessage error">
              <span>
                Whoops, there is already an account with that email. Please&nbsp;&nbsp;
                <SrLink to={loginUrl}>log&nbsp;in</SrLink>.
              </span>
            </div>
          )}
          {!lodashIsEmpty(formItem) && !onboardingApiState.inProgress && (
            <div>
              <div className="hero-image" />
              <OnboardingStep
                {...formItem}
                value={onboarding.formData[formItemId]}
                onboardingValues={onboarding.formData}
                onboardingFlowVariant={onboardingFlowVariant}
                onboardingProps={{
                  marketList: lodashValues(marketsById),
                  market: marketsById[onboarding.formData.marketId],
                }}
                onSubmit={this._onSubmitStep}
              />
              {onboarding.currentStepNumber === 1 && (
                <div className="onboardingLinkContainer">
                  <SrLink to={loginUrl}>Already have an account? Log in here →</SrLink>
                </div>
              )}
            </div>
          )}
          {onboardingApiState.inProgress && <ActivityIndicator />}
        </div>

        {showEnablePushNotificationModal && (
          <SrEnablePushNotificationsModal onClose={this._onCloseEnablePushNotificationsModal} />
        )}
      </div>
    );
  }
}

OnboardingPage.displayName = 'OnboardingPage';

export default OnboardingPage;
